import { Box, Grid } from "@mui/material";
import { useContext, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { LoadingComponent } from "../helpers/LoadingComponent";
import { API, Branch, Deal, defaultJobForImport, External1Types, Job } from "../../api";
import { Oops } from "../../utilities/oops";
import ImportTableWithMap from "./ImportTable/ImportTableWithMap";
import { External1 } from "../../utilities/integrations";
import { loadItemsButton, itemSelector, ImportFormProps } from "./ImportHelpers";
import { FileOptionTypes, GeoJSONOption } from "../../utilities/types";
import { UserContext } from "../../hooks/UserContext";
import { FeatureStats } from "./ImportTable";

export function External1ImportForm(props: ImportFormProps) {
	const userContext = useContext(UserContext);
	const [organizations, setOrganizations] = useState<External1Types.Organization[]>([]);
	const [selectedOrganizations, setSelectedOrganizations] = useState<External1Types.Organization[]>([]);
	const [growers, setGrowers] = useState<External1Types.Grower[]>([]);
	const [selectedGrowers, setSelectedGrowers] = useState<External1Types.Grower[]>([]);
	const [farms, setFarms] = useState<External1Types.Farm[]>([]);
	const [fields, setFields] = useState<External1Types.Field[]>([]);
	const [selectedFields, setSelectedFields] = useState<External1Types.Field[]>([]);
	const [selectedLabPackages] = useState<External1Types.LabPackage[]>([]);
	const [, setSamplingItems] = useState<any[]>([]);
	const [jobs, setJobs] = useState<Job[]>([]);
	const [featureStats, setFeatureStats] = useState<FeatureStats | undefined>(undefined);

	const [loading, setLoading] = useState<string | boolean>(false);

	/**
	 * Gets the Organization IDs to use.
	 * Priority: Branch IDs → Company IDs → Empty array
	 *
	 * When multiple branches are selected, combines Org IDs from all branches.
	 *
	 * @returns Array of organization IDs
	 */
	function getOrgIds(): string[] {
		try {
			// Collect Org IDs from all selected branches
			const allBranchOrgIds: string[] = [];

			for (const branch of props.selectedBranches) {
				const branchOrgIds = branch?.externalLocationId;
				if (Array.isArray(branchOrgIds) && branchOrgIds.length > 0) {
					// Add unique org IDs only
					for (const orgId of branchOrgIds) {
						if (!allBranchOrgIds.includes(orgId)) {
							allBranchOrgIds.push(orgId);
						}
					}
				}
			}

			// If any selected branches have org IDs, use them
			if (allBranchOrgIds.length > 0) {
				return allBranchOrgIds;
			}

			// Fall back to company
			const companyOrgIds = props.selectedCompany?.externalLocationId;
			if (Array.isArray(companyOrgIds) && companyOrgIds.length > 0) {
				return companyOrgIds;
			}

			// No org IDs available
			return [];
		} catch (error) {
			Oops.handleError(error, "SubmitJobs.External1ImportForm.getOrgIds");
			return [];
		}
	}

	// Reset and reload organizations when selected branch or company changes
	useEffect(() => {
		resetOrganizations();
	}, [props.selectedBranches, props.selectedCompany]);

	//#region COMPONENT
	return (
		<>
			<Grid alignItems="stretch" alignContent="stretch" container maxWidth="md" margin="auto" spacing={1}>
				{!!props.selectedDeals.length &&
					(props.user?.isSuperUser || getOrgIds().length > 1) &&
					loadItemsButton(!!loading, getOrganizations, "Load Organizations")}

				{organizations.length > 0 &&
					(props.user?.isSuperUser || getOrgIds().length > 1) &&
					itemSelector(
						!!loading,
						organizations,
						selectedOrganizations,
						handleOrganizationChange,
						setSelectedOrganizations,
						(organization) => organization.id,
						(organization) => organization.name,
						"Choose your organization(s)",
					)}

				{(!!selectedOrganizations.length || getOrgIds().length === 1) &&
					loadItemsButton(!!loading, getGrowers, "Load Growers")}

				{growers.length > 0 &&
					itemSelector(
						!!loading,
						growers,
						selectedGrowers,
						handleGrowerChange,
						setSelectedGrowers,
						(grower) => grower.id,
						(grower) => grower.name,
						"Choose your grower(s)",
					)}

				{!!selectedGrowers.length && loadItemsButton(!!loading, getFarmsAndFields, "Load Farms & Fields")}

				{fields.length > 0 &&
					itemSelector(
						!!loading,
						fields,
						selectedFields,
						handleFieldChange,
						setSelectedFields,
						(field) => field.id,
						(field) => `${farms.filter((g) => g.id == field.farm_id).at(0)?.name} - ${field.name}`,
						"Choose your field(s)",
					)}

				{!!selectedFields.length && loadItemsButton(!!loading, getSamplingEvents, "Load Jobs")}

				{loading === "submit" && (
					<Box display="flex" justifyContent="center" alignItems="center">
						<LoadingComponent />
					</Box>
				)}
			</Grid>

			{jobs.length > 0 && (
				<>
					<ImportTableWithMap
						jobs={jobs}
						updateJobs={() => {}}
						deals={props.selectedDeals}
						allDeals={props.deals}
						setLoading={(task: string | boolean) => {
							setLoading(!!task);
						}}
						allOptions={{ Boundary: [], Zones: [], Centroids: [], Cores: [], Unknown: [] }}
						resetForm={resetForm}
						integration="Agrian"
						user={userContext}
						featureStats={featureStats}
						allowMap={true}
						selectedCompany={props.selectedCompany ?? undefined}
						salespersons={props.salespersons ?? []}
						branches={props.branches ?? []}
						lastCropOptions={props.lastCropOptions}
						nextCropOptions={props.nextCropOptions}
					/>
				</>
			)}
		</>
	);
	//#endregion

	//#region CHANGE HANDLERS
	function resetForm() {
		resetOrganizations();
	}
	function handleOrganizationChange(_event: any, value: External1Types.Organization[]) {
		setSelectedOrganizations(value);
		resetGrowers();
	}
	function handleGrowerChange(_event: any, value: External1Types.Grower[]) {
		setSelectedGrowers(value);
		resetFarms();
	}
	function handleFieldChange(_event: any, value: External1Types.Field[]) {
		setSelectedFields(value);
	}

	function resetOrganizations() {
		setOrganizations([]);
		setSelectedOrganizations([]);

		resetGrowers();
	}
	function resetGrowers() {
		setGrowers([]);
		setSelectedGrowers([]);

		resetFarms();
	}
	function resetFarms() {
		setFarms([]);

		resetFields();
	}
	function resetFields() {
		setFields([]);
		setSelectedFields([]);

		resetSampleEvents();
	}
	function resetSampleEvents() {
		setSamplingItems([]);
		setJobs([]);
		setFeatureStats(undefined);
	}
	//#endregion

	//#region API CALLS
	async function getOrganizations() {
		if (!(props.selectedCompany && props.selectedCompany.agrianType)) return;
		setLoading(true);
		resetOrganizations();
		const loadingOrganizationToast = toast.loading("Loading Organizations...");
		try {
			let organizations: External1Types.Organization[] = [];
			if (props.user?.isSuperUser) {
				organizations = await API.Integrations.External1.getOrganizations(props.selectedCompany.agrianType);
			} else {
				const orgIds = getOrgIds();
				if (orgIds.length > 1) {
					organizations = await API.Integrations.External1.getOrganizationsByIds(
						orgIds,
						props.selectedCompany.agrianType,
					);
				}
			}
			const uniqueOrganizations = Array.from(new Map(organizations.map((o) => [o.id, o])).values());
			setOrganizations(uniqueOrganizations);
		} catch (error: any) {
			Oops.handleError(error, "SubmitJobs.External1ImportForm.getOrganizations");
		} finally {
			setLoading(false);
			if (loadingOrganizationToast && toast.isActive(loadingOrganizationToast)) toast.dismiss(loadingOrganizationToast);
		}
	}

	async function getGrowers() {
		if (!(props.selectedCompany && props.selectedCompany.agrianType)) return;
		setLoading(true);
		resetGrowers();
		const loadingGrowerToast = toast.loading("Loading Growers...");

		try {
			const orgIds = selectedOrganizations.map((org) => org.id);
			if (selectedOrganizations.length === 0) {
				for (const orgId of getOrgIds()) {
					if (!orgIds.includes(orgId)) {
						orgIds.push(orgId);
					}
				}
			}
			const growers = await API.Integrations.External1.getGrowers(orgIds, props.selectedCompany.agrianType);
			const uniqueGrowers = Array.from(new Map(growers.map((g) => [g.id, g])).values());
			setGrowers(uniqueGrowers);
		} catch (error: any) {
			Oops.handleError(error, "SubmitJobs.External1ImportForm.getGrowers");
		} finally {
			setLoading(false);
			if (loadingGrowerToast && toast.isActive(loadingGrowerToast)) toast.dismiss(loadingGrowerToast);
		}
	}

	async function getFarmsAndFields() {
		if (!(props.selectedCompany && props.selectedCompany.agrianType)) return;
		const loadingFarmsAndFieldsToast = toast.loading("Loading Farms and Fields...");
		setLoading(true);
		resetFarms();
		try {
			const growerIds = selectedGrowers.map((grower) => grower.id);
			const farms = await API.Integrations.External1.getFarms(growerIds, props.selectedCompany.agrianType);
			const uniqueFarms = Array.from(new Map(farms.map((f) => [f.id, f])).values());
			setFarms(uniqueFarms);

			const farmIds = uniqueFarms.map((farm) => farm.id);
			const fields = await API.Integrations.External1.getFields(farmIds, props.selectedCompany.agrianType);
			const uniqueFields = Array.from(new Map(fields.map((f) => [f.id, f])).values());
			setFields(uniqueFields);
		} catch (error: any) {
			Oops.handleError(error, "SubmitJobs.External1ImportForm.getFarmsAndFields");
		} finally {
			setLoading(false);
			if (loadingFarmsAndFieldsToast && toast.isActive(loadingFarmsAndFieldsToast))
				toast.dismiss(loadingFarmsAndFieldsToast);
		}
	}

	async function getSamplingEvents() {
		if (!(props.selectedCompany && props.selectedCompany.agrianType)) return;
		if (selectedLabPackages.length > 1) {
			toast.error("Please select only one lab test package.");
			return;
		}
		setLoading(true);
		resetSampleEvents();
		const loadingEvents = toast.loading("Loading...");

		const _growerDealMap: Record<string, Deal> = {};
		const _growerBranchMap: Record<string, Branch> = {};
		for (const grower of selectedGrowers) {
			_growerDealMap[grower.id] = props.selectedDeals[0];
			if (props.selectedBranches.length > 0) {
				_growerBranchMap[grower.id] = props.selectedBranches[0];
			}
		}

		try {
			setJobs([]);
			setFeatureStats(undefined);

			const isZoneDeal = props.selectedDeals[0].type === "Zone";

			const fieldIds = selectedFields.map((field) => field.id);

			const total = fieldIds.length + 2;
			let loaded = 0;

			if (loadingEvents) toast.update(loadingEvents, { progress: loaded / total });
			const targetEvents = await API.Integrations.External1.getTargetEvents(fieldIds, props.selectedCompany.agrianType);
			loaded++;

			if (loadingEvents) toast.update(loadingEvents, { progress: loaded / total });
			const zones = isZoneDeal
				? await API.Integrations.External1.getZones(fieldIds, props.selectedCompany.agrianType)
				: [];
			loaded++;

			// The Sample Events endpoint is very slow, so we're calling it once per field
			// to show the user a progress bar, so they know something is happening.
			const sampleEvents: External1Types.HydratedSampleEvent[] = [];
			for (let i = 0; i < fieldIds.length; i++) {
				const fieldId = fieldIds[i];
				if (loadingEvents) toast.update(loadingEvents, { progress: loaded / total });
				const fieldSampleEvents = await API.Integrations.External1.getSampleEvents(
					[fieldId],
					props.selectedCompany.agrianType,
				);
				sampleEvents.push(...fieldSampleEvents);
				loaded++;
			}

			const deal = props.selectedDeals[0];

			const newJobs: Job[] = [];
			for (const field of selectedFields) {
				const farm = farms.find((farm) => farm.id === field.farm_id);
				if (!farm) continue;
				const grower = selectedGrowers.find((grower) => grower.id === farm?.grower_id);
				if (!grower) continue;

				const boundaryGeoJSON = External1.getBoundaryGeoJSON([field.boundary_map]);

				function optionName(
					date: string,
					field: External1Types.Field,
					type: "boundary" | "centroids" | "cores" | "zones",
					index?: number,
				): string {
					const dt = new Date(date);
					const dateString = dt.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
					const suffix = index !== undefined ? ` (${index + 1})` : "";
					return `${dateString} - ${field.name} - ${type}${suffix}`;
				}

				const fieldTargetEvents = targetEvents
					.filter((event) => isEventAssociatedWithField(event, field))
					.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
				const targetCentroidsGeoJSON: GeoJSON.FeatureCollection[] = [];
				const targetZonesGeoJSON: GeoJSON.FeatureCollection[] = [];
				for (const event of fieldTargetEvents) {
					const centroidsGeoJSON = await External1.getTargetEventCentroidsGeoJSON(event);
					if (centroidsGeoJSON) targetCentroidsGeoJSON.push(centroidsGeoJSON);

					const zonesGeoJSON = await External1.getTargetEventZonesGeoJSON(event);
					if (zonesGeoJSON) targetZonesGeoJSON.push(zonesGeoJSON);
				}

				// Collect GeoJSON from sample events
				const fieldSampleEvents = sampleEvents
					.filter((event) => event.field_id === field.id)
					.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
				const sampleCentroidsGeoJSON: GeoJSON.FeatureCollection[] = [];
				const sampleCoresGeoJSON: GeoJSON.FeatureCollection[] = [];
				const sampleZonesGeoJSON: GeoJSON.FeatureCollection[] = [];
				for (const event of fieldSampleEvents) {
					const centroidsGeoJSON = await External1.getSampleEventCentroidsGeoJSON(event);
					if (centroidsGeoJSON) sampleCentroidsGeoJSON.push(centroidsGeoJSON);

					const coresGeoJSON = await External1.getSampleEventCoresGeoJSON(event);
					if (coresGeoJSON) sampleCoresGeoJSON.push(coresGeoJSON);

					const zonesGeoJSON = await External1.getSampleEventZonesGeoJSON(event);
					if (zonesGeoJSON) sampleZonesGeoJSON.push(zonesGeoJSON);
				}

				// Collect GeoJSON from zone maps
				const fieldZones = zones
					.filter((event) => event.field_id === field.id)
					.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
				const zoneMapsGeoJSON: GeoJSON.FeatureCollection[] = [];
				for (const event of fieldZones) {
					const zoneGeoJSON = await External1.getZoneMapGeoJSON(event);
					if (zoneGeoJSON) zoneMapsGeoJSON.push(zoneGeoJSON);
				}

				// Sample Events are preferred over Target Events
				const allCentroidsGeoJSON = [...sampleCentroidsGeoJSON, ...targetCentroidsGeoJSON];
				const allCoresGeoJSON = [...sampleCoresGeoJSON];
				let pointsGeoJSON: GeoJSON.FeatureCollection | undefined;
				if (isZoneDeal) {
					pointsGeoJSON = allCoresGeoJSON.length > 0 ? allCoresGeoJSON[0] : undefined;
				} else if (sampleCentroidsGeoJSON.length > 0) {
					pointsGeoJSON = sampleCentroidsGeoJSON[0];
				} else if (targetCentroidsGeoJSON.length > 0) {
					pointsGeoJSON = targetCentroidsGeoJSON[0];
				}

				// Sample Events are preferred over Target Events and Zones
				const allZonesGeoJSON = isZoneDeal ? [...sampleZonesGeoJSON, ...targetZonesGeoJSON, ...zoneMapsGeoJSON] : [];
				const zonesGeoJSON = allZonesGeoJSON.length > 0 ? allZonesGeoJSON[0] : undefined;

				const boundaryOption: GeoJSONOption = {
					name: optionName(field.created_at, field, "boundary"),
					geoJSON: boundaryGeoJSON,
				};
				const centroidsOptions: GeoJSONOption[] = allCentroidsGeoJSON.map((gj, i) => ({
					name: optionName(field.created_at, field, "centroids", i),
					geoJSON: gj,
				}));
				const coresOptions: GeoJSONOption[] = allCoresGeoJSON.map((gj, i) => ({
					name: optionName(field.created_at, field, "cores", i),
					geoJSON: gj,
				}));
				const zonesOptions: GeoJSONOption[] = allZonesGeoJSON.map((gj, i) => ({
					name: optionName(field.created_at, field, "zones", i),
					geoJSON: gj,
				}));

				const newJob: Job = defaultJobForImport(
					field.name,
					deal.depth,
					deal.density,
					grower.name,
					deal.frequencyOfMicros,
					deal.testPackage,
					farm.name,
				);

				newJob.files = {
					pointsGeoJSON: pointsGeoJSON,
					boundaryGeoJSON: boundaryGeoJSON,
					zonesGeoJSON: zonesGeoJSON,
				};

				newJob.fileOptions = {
					boundaryOptions: [{ type: FileOptionTypes["Boundary"], options: [boundaryOption] }],
					boundaryGeoJSON: boundaryGeoJSON,
					pointsOptions: [
						{ type: FileOptionTypes["Centroids"], options: centroidsOptions },
						{ type: FileOptionTypes["Cores"], options: coresOptions },
					],
					pointsGeoJSON: pointsGeoJSON,
					zonesOptions: [{ type: FileOptionTypes["Zones"], options: zonesOptions }],
					zonesGeoJSON: zonesGeoJSON,
				};
				newJob.dealIds = deal.id ? [deal.id] : [];
				newJob.labAccountNumber = getLabAccountNumber(deal, grower);
				newJob.externalFarmId = farm.id;
				newJob.fieldId = field.id;
				newJob.fieldPriority = "Regular Turn (default)";
				newJob.submitterEmail = props.user?.email;
				newJob.submitterName = props.user?.name;
				newJob.branchIds = props.selectedBranches.map((branch) => branch.id);
				newJob.externalGrower = {
					id: grower.id,
					name: grower.name,
				};
				newJob.eventId = ""; // this is intentionally blank. We do not want to pull in original event ID. Bnd Calc handles event ID.

				newJobs.push(newJob);
			}

			setFeatureStats({
				boundaries: newJobs.length,
				zones: newJobs.filter((job) => job.files.zonesGeoJSON?.features?.length).length,
				centroids: newJobs.filter((job) => (isZoneDeal ? false : job.files.pointsGeoJSON?.features?.length)).length,
				cores: newJobs.filter((job) => (isZoneDeal ? job.files.pointsGeoJSON?.features?.length : false)).length,
			});

			setJobs(newJobs);
		} catch (error: any) {
			Oops.handleError(error, "SubmitJobs.External1ImportForm.getSamplingEvents");
		} finally {
			setLoading(false);
			if (loadingEvents && toast.isActive(loadingEvents)) toast.dismiss(loadingEvents);
		}

		function isEventAssociatedWithField(
			event: External1Types.HydratedTargetEvent,
			field: External1Types.Field,
		): boolean {
			const associationFieldId = event.associations?.["agrian.field"];
			return (
				!!event.associations &&
				associationFieldId &&
				((typeof associationFieldId === "string" && associationFieldId === field.id) ||
					(Array.isArray(associationFieldId) && associationFieldId.includes(field.id)))
			);
		}

		function getLabAccountNumber(deal: Deal, grower: External1Types.Grower): string | undefined {
			const labAccountNumber_Deal = deal.labAccountNumber;
			let labAccountNumber = labAccountNumber_Deal;
			if (labAccountNumber_Deal?.toLowerCase().includes("branch")) {
				const branch = _growerBranchMap[grower.id];
				if (branch) {
					const labAccountNumber_Branch = branch.labAccountNumber;
					if (labAccountNumber_Branch) {
						labAccountNumber = labAccountNumber_Branch;
					}
				}
			}
			return labAccountNumber;
		}
	}
	//#endregion
}
